<template>
  <div class="login-page">
    <!-- Blue overlay (NO blur) -->
    <div class="login-overlay">
      <!-- ✅ ADDED: transition wrapper -->
      <transition name="fade-up" appear>
        <!-- ✅ ADDED: v-show to trigger animation -->
        <div v-show="show" class="content-wrap">
          <!-- Landing text -->
          <div class="brand-block text-center q-mb-xl">
            <div class="text-h4 text-weight-bold">AI Assistant for Teams</div>
            <div class="text-subtitle1 q-mt-sm">
              Multilingual AI for email and document generation
            </div>
          </div>

          <!-- Login Card -->
          <q-card class="login-card">
            <q-card-section>
              <div class="text-h5 text-center q-mb-md">Login</div>

              <q-input
                ref="usernameRef"
                filled
                v-model="username"
                label="Email or Username"
                class="q-mb-md"
                autocomplete="username"
                autocapitalize="none"
                spellcheck="false"
                inputmode="text"
              />

              <q-input
                filled
                v-model="password"
                label="Password"
                :type="showPwd ? 'text' : 'password'"
                class="q-mb-sm"
                autocomplete="current-password"
                autocapitalize="none"
                spellcheck="false"
                @keyup.enter="login"
              >
                <template #append>
                  <q-icon
                    :name="showPwd ? 'visibility_off' : 'visibility'"
                    class="cursor-pointer"
                    @click="showPwd = !showPwd"
                  />
                </template>
              </q-input>

              <div class="row items-center q-mb-md">
                <q-space />
                <q-btn
                  flat
                  dense
                  label="Forgot password?"
                  @click="forgotDialog = true"
                />
              </div>

              <q-btn
                label="Login"
                color="primary"
                unelevated
                size="lg"
                class="full-width"
                :loading="loading"
                @click="login"
              />

              <div v-if="error" class="text-negative q-mt-md">{{ error }}</div>
            </q-card-section>
          </q-card>
        </div>
      </transition>
    </div>

    <!-- Forgot password dialog -->
    <q-dialog v-model="forgotDialog">
      <q-card class="forgot-card">
        <q-card-section class="text-h6">Reset your password</q-card-section>

        <q-card-section class="q-gutter-md">
          <div class="text-body2 text-grey-7">
            Enter your account email address. We’ll send a reset link.
          </div>

          <q-input
            outlined
            v-model="forgotEmail"
            label="Email"
            type="email"
            autocomplete="email"
            autocapitalize="none"
            spellcheck="false"
            inputmode="email"
          />

          <div v-if="forgotMsg" class="text-positive">{{ forgotMsg }}</div>
          <div v-if="forgotErr" class="text-negative">{{ forgotErr }}</div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" v-close-popup />
          <q-btn
            color="primary"
            label="Send link"
            :loading="forgotLoading"
            @click="sendResetLink"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { api } from '@/api';

const router = useRouter();

const username = ref('');
const password = ref('');
const showPwd = ref(false);

const loading = ref(false);
const error = ref('');

const forgotDialog = ref(false);
const forgotEmail = ref('');
const forgotLoading = ref(false);
const forgotMsg = ref('');
const forgotErr = ref('');

const usernameRef = ref(null);

/* ✅ ADDED: controls the animation */
const show = ref(false);

onMounted(() => {
  /* ✅ ADDED: triggers transition after mount */
  requestAnimationFrame(() => {
    show.value = true;
  });

  // Slight delay helps avoid mobile focus flicker
  setTimeout(() => {
    usernameRef.value?.focus?.();
  }, 150);
});

async function login() {
  if (!navigator.onLine) {
    error.value =
      'You are offline. Please connect to the internet and try again.';
    return;
  }

  loading.value = true;
  error.value = '';

  try {
  const res = await api.post('token/', {
    username: username.value,
    password: password.value,
  });

  localStorage.setItem('access', res.data.access);
  localStorage.setItem('refresh', res.data.refresh);

  // load /me (admin UI etc.)
  try {
    const meRes = await api.get('iam/users/me/');
    localStorage.setItem('me', JSON.stringify(meRes.data));

    // Check if user has groups/permissions
    const groups = meRes.data.groups || [];
    const permissions = meRes.data.permissions || [];

    // Users without permissions → Email page
    if (!groups.length && !permissions.length) {
      router.push('/email');
    } else {
      // Users with permissions → Email page (or /iam/users for admins)
      router.push('/email');
    }
  } catch {
    localStorage.removeItem('me');
    router.push('/email'); // fallback
  }
} catch (e) {
  error.value = 'Invalid email/username or password';
} finally {
  loading.value = false;
}
}

async function sendResetLink() {
  if (!navigator.onLine) {
    forgotErr.value =
      'You are offline. Please connect to the internet and try again.';
    return;
  }

  forgotLoading.value = true;
  forgotMsg.value = '';
  forgotErr.value = '';

  try {
    await api.post('iam/password-reset/', { email: forgotEmail.value });
    forgotMsg.value = 'If that email exists, a reset link has been sent.';

    // Clear the form and close dialog after successful submission
    setTimeout(() => {
      forgotEmail.value = '';
      forgotMsg.value = '';
      forgotErr.value = '';
      forgotDialog.value = false;
    }, 3000); // Give user time to read the message
  } catch (e) {
    // keep message generic for security
    forgotMsg.value = 'If that email exists, a reset link has been sent.';
    console.warn(e?.response?.data);

    // Still clear the form and close dialog even on error (for UX)
    setTimeout(() => {
      forgotEmail.value = '';
      forgotMsg.value = '';
      forgotErr.value = '';
      forgotDialog.value = false;
    }, 3000);
  } finally {
    forgotLoading.value = false;
  }
}
</script>

<style scoped>
/*
  IMPORTANT:
  Save your image here:
    src/assets/login-bg.jpg
*/

.login-page {
  min-height: 100vh; /* fallback */
  min-height: 100dvh; /* better on mobile */
  background-image: url('@/assets/login-bg.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  display: flex;
  align-items: center;
  justify-content: center;

  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
}

/* SoftSmartTech blue overlay (NO blur => background stays sharp) */
.login-overlay {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;

  background: linear-gradient(
    135deg,
    rgba(25, 118, 210, 0.35) 0%,
    rgba(13, 71, 161, 0.45) 50%,
    rgba(10, 25, 60, 0.55) 100%
  );

  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.content-wrap {
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.brand-block {
  color: #ffffff;
  padding: 0 12px;
  text-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
}

.login-card {
  width: 100%;
  max-width: 420px;
  border-radius: 14px;
  background: #ffffff; /* solid card = crisp */
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.35);
}

/* Make Quasar "filled" inputs look clean on white */
.login-card :deep(.q-field--filled .q-field__control) {
  background: #f5f8ff;
}

/* Forgot dialog responsive width */
.forgot-card {
  width: min(92vw, 420px);
}

/* ✅ ADDED: Entrance animation CSS */
.fade-up-enter-active {
  transition: opacity 320ms ease, transform 320ms ease;
}
.fade-up-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.fade-up-enter-to {
  opacity: 1;
  transform: translateY(0);
}

/* Mobile tweaks */
@media (max-width: 600px) {
  .login-card {
    max-width: 94vw;
  }

  /* shift background focal area slightly if needed */
  .login-page {
    background-position: 70% center;
  }
}
</style>
